import React, { useEffect, useRef, useState } from "react";
import { MoreHoriz, Send } from "@mui/icons-material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import {
  Box,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Chip,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { motion } from "framer-motion";
import httpcommon from "../../helper/httpcommon";
import CommonButtonActionButton from "./CommonButtonActionButton";

const MotionBox = motion(Box);

/* ðŸ”¥ STATIC CATEGORY LIST */
const CATEGORY_LIST = [
  { key: "Spatial_Aptitude", label: "Spatial Aptitude" },
  { key: "Verbal_Aptitude", label: "Verbal Aptitude" },
  { key: "Attention_to_detail", label: "Attention To Detail" },
  { key: "Technical_Aptitude", label: "Technical Aptitude" },
  { key: "Situational_Judgement", label: "Situational Judgement" },
  { key: "Abstract_Reasoning", label: "Abstract Reasoning" },
  { key: "Logical_Reasoning", label: "Logical Reasoning" },
  { key: "Mechanical_Aptitude", label: "Mechanical Aptitude" },
  { key: "General_knowledge_and_awareness", label: "General Knowledge" },
  { key: "Numerical_Reasoning", label: "Numerical Reasoning" },
];
const QUESTIONS_PER_APTITUDE_SECTION = 5;

/**
 * Scans conversation history to resolve category state from backend auto-selection.
 *
 * API sends system messages like:
 *   { sender: "system", message_type: "category_selection", content: "Your Test Category : Spatial Aptitude" }
 *
 * We extract the label after "Your Test Category : " and match it against CATEGORY_LIST.label
 *
 * Returns:
 *   selectedCategory  â†’ key of the LAST/current active category
 *   allSelectedKeys   â†’ keys of ALL categories selected so far (for "done" state)
 *   nextStepIndex     â†’ index after the last selected category (next card position)
 */
const normalizeCat = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[_\s]+/g, " ")
    .trim();

const resolveCategoryFromHistory = (conversation = []) => {
  const allSelectedKeys = [];

  for (let i = 0; i < conversation.length; i++) {
    const msg = conversation[i];

    if (msg.sender === "system" && msg.message_type === "category_selection") {
      // content is "Your Test Category : Spatial Aptitude"
      // (older sessions used "You have chosen: ..." â€” keep both for compatibility)
      const label = msg.content
        .replace("Your Test Category :", "")
        .replace("You have chosen:", "")
        .trim();
      // Match on a normalized form so backend display names like
      // "General Knowledge And Awareness" still map to the "General Knowledge"
      // entry (whose key is General_knowledge_and_awareness).
      const target = normalizeCat(label);
      const matched = CATEGORY_LIST.find(
        (cat) =>
          normalizeCat(cat.label) === target ||
          normalizeCat(cat.key) === target
      );

      if (matched && !allSelectedKeys.includes(matched.key)) {
        allSelectedKeys.push(matched.key);
      }
    }
  }

  const lastKey = allSelectedKeys[allSelectedKeys.length - 1] || null;
  const lastIndex = lastKey
    ? CATEGORY_LIST.findIndex((cat) => cat.key === lastKey)
    : -1;

  return {
    selectedCategory: lastKey,         // current active category key
    allSelectedKeys,                    // all previously completed category keys
    nextStepIndex: lastIndex + 1,       // index of next card to highlight
  };
};

const Chatbot = ({
  triggerNewConversion,
  student,
  selectSubject,
  variant,
  deferInitialLoad = false,
  onComplete = () => { },
}) => {
  const theme = useTheme();
  useMediaQuery(theme.breakpoints.down("xs"));
  const cardRefs = useRef([]);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [messages, setMessages] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [userInput, setUserInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [isTestCompleted, setIsTestCompleted] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [allSelectedKeys, setAllSelectedKeys] = useState([]);
  const [apiError, setApiError] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [displayQuestionIndex, setDisplayQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  // The question count that existed when Next was pressed. This prevents a
  // render with the old conversation from consuming the navigation request.
  const [advanceAfterQuestionCount, setAdvanceAfterQuestionCount] = useState(null);

  const isAssessment = variant === "assessment";

  /* -------------------- Effects -------------------- */

  useEffect(() => {
    if (!deferInitialLoad) startSession();
  }, [selectSubject, deferInitialLoad]);

  useEffect(() => {
    if (triggerNewConversion != null) {
      sendMessageToApi("new", true);
    }
  }, [triggerNewConversion]);

  useEffect(() => {
    scrollToMessage();
  }, [messages]);

  // Auto-scroll category row to active card
  useEffect(() => {
    const activeRef = cardRefs.current[currentStepIndex];
    if (activeRef) {
      activeRef.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [currentStepIndex, currentQuestion]);

  /* -------------------- Scroll -------------------- */

  const scrollToMessage = () => {
    const el = document.getElementById(`message-${messages.length - 1}`);
    el?.scrollIntoView({ behavior: "smooth" });
  };

  /* -------------------- Sync Category State -------------------- */

  const syncCategoryState = (conversation = []) => {
    const { selectedCategory: cat, allSelectedKeys: keys, nextStepIndex } =
      resolveCategoryFromHistory(conversation);
    setSelectedCategory(cat);
    setAllSelectedKeys(keys);
    setCurrentStepIndex(nextStepIndex);
  };

  /* -------------------- API -------------------- */

  const startSession = async () => {
    try {
      setIsLoadingChat(true);
      const res = await httpcommon.get(
        `/school/chat/?test_type=${selectSubject}`
      );

      const { conversation, session_id, next_question, is_complete, message } =
        res.data;

      setMessages(conversation || []);
      setSessionId(session_id);
      setCurrentQuestion(next_question || null);
      setIsTestCompleted(is_complete || message === "Test completed!");
      setApiError(null);
      setDisplayQuestionIndex(0);
      setSelectedAnswers({});
      setAdvanceAfterQuestionCount(null);

      // âœ… Resolve category state from conversation history
      syncCategoryState(conversation || []);
    } catch (err) {
      console.error(err);
      setApiError(
        "We couldn't load your test. Please check your connection and try again."
      );
    } finally {
      setIsLoadingChat(false);
    }
  };

  const sendMessageToApi = async (message, newConversion = false, questionId = null) => {
    try {
      setIsLoadingChat(true);

      const payload = newConversion
        ? { message: "start", test_type: selectSubject }
        : { session_id: sessionId, message, test_type: selectSubject, question_id: questionId ?? undefined };

      const res = await httpcommon.post("/school/chat/", payload);

      if (res.data.status === "success") {
        const {
          conversation,
          session_id,
          next_question,
          is_complete,
          message,
        } = res.data;

        setMessages(conversation || []);
        setSessionId(session_id);
        setCurrentQuestion(next_question || null);

        if (newConversion) {
          // Fresh start â€” reset everything
          setIsTestCompleted(false);
          setCurrentStepIndex(0);
          setSelectedCategory(null);
          setAllSelectedKeys([]);
          setDisplayQuestionIndex(0);
          setSelectedAnswers({});
          setAdvanceAfterQuestionCount(null);
        } else {
          setIsTestCompleted(is_complete || message === "Test completed!");
          // âœ… Re-sync category state after every backend response
          syncCategoryState(conversation || []);
        }
        setApiError(null);
      } else {
        // Non-success payload â€” surface it instead of silently stalling
        setApiError(
          res.data?.message ||
            "Something went wrong loading the next question. Please try again."
        );
      }
    } catch (err) {
      console.error(err);
      setApiError(
        "Something went wrong loading the next question. Please try again."
      );
    } finally {
      setIsLoadingChat(false);
    }
  };

  /* -------------------- Handlers -------------------- */

  const isCategorySelection = currentQuestion?.is_category_selection;

  const handleOptionClick = async (optionValue) => {
    if (isAssessment) {
      setSelectedAnswers((previous) => ({
        ...previous,
        // The visible question can be clamped while new conversation data is
        // arriving, so store the choice against the resolved question index.
        [activeQuestionIndex]: optionValue,
      }));
      return;
    }

    const userMessage = {
      sender: "student",
      content: `Option selected: ${optionValue}`,
      message_type: "answer",
    };
    setMessages((prev) => [...prev, userMessage]);
    await sendMessageToApi(optionValue);
  };

  const handleTextSubmit = async () => {
    if (!userInput.trim()) return;

    const userMessage = {
      sender: "student",
      content: userInput,
      message_type: "answer",
    };

    setMessages((prev) => [...prev, userMessage]);
    setUserInput("");
    await sendMessageToApi(userInput);
  };

  /* -------------------- Derived (display only) -------------------- */

  const answeredQuestions = messages.filter(
    (m) => m.sender === "system" && m.message_type === "question"
  ).length;
  const categoryProgress = selectSubject === "adaptive"
    ? Math.round((allSelectedKeys.length / CATEGORY_LIST.length) * 100)
    : 0;
  const activeCatLabel =
    CATEGORY_LIST.find((c) => c.key === selectedCategory)?.label || null;
  const sectionNumber = Math.min(
    Math.max(allSelectedKeys.length, 1),
    CATEGORY_LIST.length
  );

  const optionLetter = (i) => String.fromCharCode(65 + i);

  /* -------------------- Assessment focused view (display-only) -------------------- */

  const isAdaptive = selectSubject === "adaptive";

  const questionMsgs = messages.filter(
    (m) => m.sender === "system" && m.message_type === "question"
  );
  const currentCategoryStartIndex = messages.reduce(
    (latestIndex, message, index) => (
      message.sender === "system" && message.message_type === "category_selection"
        ? index
        : latestIndex
    ),
    -1
  );
  const activeQuestionIndex = questionMsgs.length
    ? Math.min(displayQuestionIndex, questionMsgs.length - 1)
    : 0;
  const displayedQuestion = questionMsgs[activeQuestionIndex] || null;
  const currentSectionQuestions = messages
    .slice(currentCategoryStartIndex + 1)
    .filter((message) => message.sender === "system" && message.message_type === "question");
  const sectionQuestionNumber = isAdaptive
    ? Math.max(currentSectionQuestions.indexOf(displayedQuestion) + 1, currentQuestion ? 1 : 0)
    : 0;
  const isLatestQuestion = activeQuestionIndex === questionMsgs.length - 1;
  const displayedOptions = isLatestQuestion
    ? currentQuestion?.options || displayedQuestion?.options
    : displayedQuestion?.options;
  const currentQuestionText = displayedQuestion?.content || null;
  const currentQuestionNumber = questionMsgs.length ? activeQuestionIndex + 1 : 0;
  const persistedAnswer = displayedQuestion
    ? messages.slice(messages.indexOf(displayedQuestion) + 1).find(
      (message) => message.sender === "student" && message.message_type === "answer"
    )
    : null;
  const persistedAnswerKey = persistedAnswer
    ? Object.entries(displayedOptions || {}).find(
      ([key, value]) => key === persistedAnswer.content || String(value) === persistedAnswer.content
    )?.[0]
    : null;
  const selectedAnswer = selectedAnswers[activeQuestionIndex] || persistedAnswerKey;

  const PERSONALITY_TOTAL = 20;
  const ringValue = isAdaptive
    ? allSelectedKeys.length
    : Math.min(currentQuestionNumber, PERSONALITY_TOTAL);
  const ringTotal = isAdaptive ? CATEGORY_LIST.length : PERSONALITY_TOTAL;
  const ringPct = ringTotal ? (ringValue / ringTotal) * 100 : 0;
  const topBarPct = isAdaptive
    ? categoryProgress
    : Math.min((currentQuestionNumber / PERSONALITY_TOTAL) * 100, 100);

  const RING_R = 46;
  const RING_C = 2 * Math.PI * RING_R;

  const showQuestion =
    !isTestCompleted && !isCategorySelection && !!currentQuestionText;
  const showOptions =
    showQuestion && !isCategorySelection && !!displayedOptions;

  useEffect(() => {
    if (
      advanceAfterQuestionCount !== null &&
      questionMsgs.length > advanceAfterQuestionCount
    ) {
      setDisplayQuestionIndex(questionMsgs.length - 1);
      setAdvanceAfterQuestionCount(null);
    }
  }, [advanceAfterQuestionCount, questionMsgs.length]);

  const handlePreviousQuestion = () => {
    setDisplayQuestionIndex((index) => Math.max(0, index - 1));
  };

  const handleNextQuestion = async () => {
    if (!isLatestQuestion) {
      if (selectedAnswers[activeQuestionIndex] && selectedAnswers[activeQuestionIndex] !== persistedAnswerKey && displayedQuestion?.question_id) {
        await sendMessageToApi(selectedAnswers[activeQuestionIndex], false, displayedQuestion.question_id);
      }
      setDisplayQuestionIndex((index) => Math.min(questionMsgs.length - 1, index + 1));
      return;
    }
    if (!selectedAnswer || isLoadingChat) return;
    setAdvanceAfterQuestionCount(questionMsgs.length);
    await sendMessageToApi(selectedAnswer, false, displayedQuestion?.question_id);
  };

  // Latest system status line (used when there is nothing to answer)
  const latestSystemNote = [...messages]
    .reverse()
    .find(
      (m) =>
        m.sender === "system" &&
        ["message", "completion", "category_selection"].includes(m.message_type)
    )?.content;

  const handleRetry = () => {
    setApiError(null);
    startSession();
  };

  const handleConfirmReset = () => {
    setResetOpen(false);
    setApiError(null);
    // Same path as the "Start Test" trigger â€” spins up a fresh session.
    sendMessageToApi("new", true);
  };

  if (isAssessment) {
    return (
      <div className="chat-container chat--assessment apt-shell">
        <Box
          sx={{
            width: "100%",
            borderRadius: "22px",
            overflow: "hidden",
            border: "1px solid #E7EAF1",
            boxShadow: "0 18px 48px rgba(41, 45, 91, 0.08)",
            backgroundColor: "#fff",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "330px 1fr" },
            minHeight: { xs: "auto", md: 560 },
          }}
        >
          {/* â”€â”€ Left rail â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          <Box
            sx={{
              position: "relative",
              background:
                "linear-gradient(160deg, #262B61 0%, #363C7C 55%, #4B3E86 100%)",
              color: "#fff",
              p: { xs: 2.5, md: 3.5 },
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -70,
                right: -70,
                width: 200,
                height: 200,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(246,160,58,0.35) 0%, rgba(246,160,58,0) 70%)",
              }}
            />

            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Typography
                sx={{
                  fontSize: 10.5,
                  fontWeight: 800,
                  letterSpacing: "2px",
                  opacity: 0.7,
                }}
              >
                {isAdaptive ? "APTITUDE" : "SELF-INTROSPECTION"}
              </Typography>
              <Typography
                sx={{ fontSize: { xs: 20, md: 23 }, fontWeight: 800, mt: 1.5, lineHeight: 1.2 }}
              >
                {isAdaptive ? activeCatLabel || "Aptitude Test" : "A look inward"}
              </Typography>
              <Typography
                sx={{ fontSize: 13, opacity: 0.78, mt: 1, lineHeight: 1.6, maxWidth: 260 }}
              >
                {isAdaptive
                  ? "Ten short sections that adapt to how you answer. Trust your instinct."
                  : "Honest answers only - there is no right or wrong here."}
              </Typography>
            </Box>

            {/* Progress ring */}
            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                mt: { xs: 3, md: "auto" },
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box sx={{ position: "relative", width: 108, height: 108, flexShrink: 0 }}>
                <Box
                  component="svg"
                  viewBox="0 0 108 108"
                  sx={{ width: "100%", height: "100%", transform: "rotate(-90deg)" }}
                >
                  <circle
                    cx="54"
                    cy="54"
                    r={RING_R}
                    fill="none"
                    stroke="rgba(255,255,255,0.16)"
                    strokeWidth="7"
                  />
                  <MotionBox
                    component="circle"
                    cx="54"
                    cy="54"
                    r={RING_R}
                    fill="none"
                    stroke="#F6A03A"
                    strokeWidth="7"
                    strokeLinecap="round"
                    strokeDasharray={RING_C}
                    animate={{ strokeDashoffset: RING_C * (1 - ringPct / 100) }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                  />
                </Box>
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1 }}>
                    {ringValue}
                  </Typography>
                  <Typography sx={{ fontSize: 11, opacity: 0.7 }}>
                    {isAdaptive ? `of ${ringTotal}` : "answered"}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                {isAdaptive && !isTestCompleted && (
                  <Box
                    sx={{
                      mb: 1.35,
                      pb: 1.25,
                      borderBottom: "1px solid rgba(255,255,255,0.18)",
                    }}
                  >
                    <Typography sx={{ fontSize: 19, fontWeight: 800, lineHeight: 1.1, letterSpacing: "0.1px" }}>
                      {Math.min(sectionQuestionNumber, QUESTIONS_PER_APTITUDE_SECTION)}
                      <Box component="span" sx={{ fontSize: 12, fontWeight: 700, opacity: 0.72, ml: 0.55 }}>
                        of {QUESTIONS_PER_APTITUDE_SECTION}
                      </Box>
                    </Typography>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.25px", opacity: 0.64, mt: 0.35 }}>
                      Question in this section
                    </Typography>
                  </Box>
                )}
                <Typography sx={{ fontSize: 12.5, fontWeight: 750, lineHeight: 1.35, opacity: 0.94 }}>
                  {isAdaptive
                    ? `${activeCatLabel || "Starting"} - Section ${sectionNumber} / ${CATEGORY_LIST.length}`
                    : `Question ${currentQuestionNumber || 1}`}
                </Typography>
                <Typography sx={{ fontSize: 11.5, opacity: 0.64, mt: 0.45 }}>
                  {isTestCompleted
                    ? "All done"
                    : isAdaptive
                      ? "Current aptitude test"
                      : "keep going"}
                </Typography>
              </Box>
            </Box>

            {/* Section dots (adaptive only) */}
            {isAdaptive && (
              <Box
                sx={{
                  position: "relative",
                  zIndex: 1,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.9,
                  mt: 2.5,
                }}
              >
                {CATEGORY_LIST.map((cat, i) => {
                  const active = !isTestCompleted && cat.key === selectedCategory;
                  const done =
                    isTestCompleted ||
                    (allSelectedKeys.includes(cat.key) && !active);
                  return (
                    <Box
                      key={cat.key}
                      title={cat.label}
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "7px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 10.5,
                        fontWeight: 800,
                        color: done || active ? "#fff" : "rgba(255,255,255,0.6)",
                        backgroundColor: done
                          ? "#2E7D32"
                          : active
                            ? "#F6A03A"
                            : "rgba(255,255,255,0.12)",
                        border: active ? "2px solid #fff" : "2px solid transparent",
                        transition: "all .25s ease",
                      }}
                    >
                      {i + 1}
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* Reset / retake */}
            <Button
              onClick={() => setResetOpen(true)}
              disabled={isLoadingChat}
              startIcon={<RestartAltRoundedIcon sx={{ fontSize: 16 }} />}
              sx={{
                position: "relative",
                zIndex: 1,
                alignSelf: "flex-start",
                mt: isAdaptive ? 2.5 : 3,
                px: 1.25,
                py: 0.5,
                borderRadius: "999px",
                textTransform: "none",
                fontSize: 12,
                fontWeight: 700,
                color: "rgba(255,255,255,0.85)",
                backgroundColor: "rgba(255,255,255,0.1)",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.18)" },
                "&.Mui-disabled": { color: "rgba(255,255,255,0.4)" },
              }}
            >
              Restart test
            </Button>
          </Box>

          {/* â”€â”€ Right panel â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              backgroundColor: "#FCFCFE",
            }}
          >
            <Box sx={{ height: 4, backgroundColor: "#EEF0F5", flexShrink: 0 }}>
              <MotionBox
                animate={{ width: `${topBarPct}%` }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                sx={{
                  height: "100%",
                  background: "linear-gradient(90deg, #F6A03A, #F7B65C)",
                }}
              />
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                px: { xs: 2.5, md: 4.5 },
                pt: { xs: 3, md: 4 },
                pb: 3,
              }}
            >
              <>
                {apiError && !isLoadingChat && !isTestCompleted ? (
                  <MotionBox
                    key="err"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    sx={{ textAlign: "center", py: { xs: 3, md: 5 }, maxWidth: 420, mx: "auto" }}
                  >
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        mx: "auto",
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#FEECEC",
                      }}
                    >
                      <CloseRoundedIcon sx={{ color: "#E5484D", fontSize: 30 }} />
                    </Box>
                    <Typography sx={{ fontSize: 17, fontWeight: 800, color: "#292D5B" }}>
                      That didn't go through
                    </Typography>
                    <Typography
                      sx={{ fontSize: 13.5, color: "#5B6178", mt: 1, lineHeight: 1.6 }}
                    >
                      {latestSystemNote || apiError}
                    </Typography>
                    <CommonButtonActionButton
                      onClick={handleRetry}
                      sx={{
                        mt: 2.5,
                        minWidth: 150,
                        borderRadius: "12px",
                        fontWeight: 700,
                        textTransform: "none",
                      }}
                    >
                      Try again
                    </CommonButtonActionButton>
                  </MotionBox>
                ) : isTestCompleted ? (
                  <MotionBox
                    key="done"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    sx={{ textAlign: "center", py: { xs: 3, md: 5 } }}
                  >
                    <Box
                      sx={{
                        width: 68,
                        height: 68,
                        borderRadius: "50%",
                        mx: "auto",
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background:
                          "linear-gradient(135deg, #F6A03A 0%, #F7B65C 100%)",
                        boxShadow: "0 12px 28px rgba(246,160,58,0.35)",
                      }}
                    >
                      <EmojiEventsRoundedIcon sx={{ color: "#fff", fontSize: 34 }} />
                    </Box>
                    <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#292D5B" }}>
                      {isAdaptive ? "Aptitude test complete!" : "Introspection complete!"}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 14, color: "#5B6178", mt: 1, maxWidth: 380, mx: "auto", lineHeight: 1.6 }}
                    >
                      Nice work - every answer is saved. Hit submit to lock it in and
                      move to the next step.
                    </Typography>
                  </MotionBox>
                ) : !showQuestion ? (
                  <MotionBox
                    key="prep"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 2,
                      py: { xs: 5, md: 8 },
                    }}
                  >
                    <CircularProgress sx={{ color: "#F6A03A" }} />
                    <Typography sx={{ fontSize: 14, color: "#8A90A3", fontWeight: 600 }}>
                      {isAdaptive
                        ? "Setting up your first section..."
                        : "Loading your questions..."}
                    </Typography>
                  </MotionBox>
                ) : (
                  <MotionBox
                    key={`q-${currentQuestionNumber}`}
                    initial={{ opacity: 0, x: 22 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.28, ease: "easeOut" }}
                  >
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 800,
                        letterSpacing: "0.5px",
                        color: "#9AA0B2",
                        mb: 1,
                      }}
                    >
                      QUESTION {currentQuestionNumber}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: { xs: 18, md: 21 },
                        fontWeight: 800,
                        color: "#292D5B",
                        lineHeight: 1.4,
                        mb: displayedQuestion?.image_url ? 2 : 3,
                        maxWidth: 640,
                      }}
                    >
                      {currentQuestionText}
                    </Typography>

                    {displayedQuestion?.image_url && (
                      <Box
                        component="img"
                        src={displayedQuestion.image_url}
                        alt="question"
                        sx={{
                          maxWidth: 320,
                          width: "100%",
                          borderRadius: "16px",
                          border: "1px solid #E7EAF1",
                          background: "#fff",
                          p: 1,
                          mb: 3,
                          objectFit: "contain",
                        }}
                      />
                    )}

                    {showOptions && (
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                          gap: 1.25,
                          maxWidth: 700,
                          opacity: isLoadingChat ? 0.55 : 1,
                          pointerEvents: isLoadingChat ? "none" : "auto",
                          transition: "opacity .2s ease",
                        }}
                      >
                        {Object.entries(displayedOptions).map(
                          ([key, val], index) => (
                            <Box
                              key={key}
                              onClick={() => !isLoadingChat && handleOptionClick(key)}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                p: 1.85,
                                borderRadius: "14px",
                                border: selectedAnswer === key ? "1.5px solid #F6A03A" : "1.5px solid #E7EAF1",
                                backgroundColor: selectedAnswer === key ? "#FFF6E8" : "#fff",
                                cursor: "pointer",
                                transition: "all .16s ease",
                                "&:hover": {
                                  borderColor: "#F6A03A",
                                  backgroundColor: "#FFF9F0",
                                  transform: "translateY(-2px)",
                                  boxShadow: "0 10px 24px rgba(246,160,58,0.16)",
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  flexShrink: 0,
                                  width: 30,
                                  height: 30,
                                  borderRadius: "9px",
                                  backgroundColor: "#F1F0F8",
                                  color: selectedAnswer === key ? "#A85B05" : "#292D5B",
                                  fontSize: 13.5,
                                  fontWeight: 800,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                {optionLetter(index)}
                              </Box>
                              <Typography
                                sx={{
                                  fontSize: 14.5,
                                  fontWeight: 500,
                                  color: "#2C3350",
                                  lineHeight: 1.45,
                                }}
                              >
                                {val}
                              </Typography>
                            </Box>
                          )
                        )}
                      </Box>
                    )}

                    {isLoadingChat && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 2,
                          color: "#8A90A3",
                        }}
                      >
                        <CircularProgress size={16} sx={{ color: "#F6A03A" }} />
                        <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                          Saving your answer...
                        </Typography>
                      </Box>
                    )}
                  </MotionBox>
                )}
              </>
            </Box>

            {/* Footer */}
            <Box
              sx={{
                flexShrink: 0,
                px: { xs: 2.5, md: 4.5 },
                py: 2,
                borderTop: "1px solid #EEF0F5",
                backgroundColor: "#fff",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              {isTestCompleted ? (
                <CommonButtonActionButton
                  onClick={onComplete}
                  sx={{ ml: "auto", minWidth: 150, borderRadius: "12px", fontWeight: 700, textTransform: "none", boxShadow: "0 10px 22px rgba(246,160,58,0.32)" }}
                >
                  Submit
                </CommonButtonActionButton>
              ) : (
                <>
                  <Button onClick={handlePreviousQuestion} disabled={activeQuestionIndex === 0 || isLoadingChat} sx={{ minWidth: 92, borderRadius: "12px", fontWeight: 700, textTransform: "none", color: "#565D78", border: "1px solid #DCE0EA" }}>
                    Back
                  </Button>
                  <CommonButtonActionButton
                    onClick={handleNextQuestion}
                    disabled={isLatestQuestion && (!selectedAnswer || isLoadingChat)}
                    sx={{ ml: "auto", minWidth: 110, borderRadius: "12px", fontWeight: 700, textTransform: "none" }}
                  >
                    {isLoadingChat && isLatestQuestion ? "Saving..." : "Next"}
                  </CommonButtonActionButton>
                </>
              )}
            </Box>
          </Box>
        </Box>

        {/* Restart confirmation */}
        <Dialog
          open={resetOpen}
          onClose={() => setResetOpen(false)}
          PaperProps={{ sx: { borderRadius: "18px", p: 1, maxWidth: 380 } }}
        >
          <DialogTitle sx={{ fontWeight: 800, color: "#292D5B" }}>
            Restart the {isAdaptive ? "aptitude test" : "test"}?
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: 14, color: "#3E4A66", lineHeight: 1.7 }}>
              This clears all your current answers and starts the test again from
              the first question. This can't be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setResetOpen(false)}
              sx={{
                borderRadius: "100px",
                textTransform: "none",
                fontWeight: 700,
                color: "#7A8096",
                px: 2.5,
              }}
            >
              Keep going
            </Button>
            <CommonButtonActionButton
              onClick={handleConfirmReset}
              sx={{
                borderRadius: "100px",
                minWidth: 150,
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              Yes, restart
            </CommonButtonActionButton>
          </DialogActions>
        </Dialog>
      </div>
    );
  }

  return (
    <div className={`chat-container${isAssessment ? " chat--assessment" : ""}`}>

      {/* -------------------- Category Scroll Row -------------------- */}
      {selectSubject === "adaptive" && (
        <Box
          sx={{
            width: {
              xs: "320px",
              sm: "500px",
              md: "700px",
              lg: "980px",
              xl: "1280px",
            },
            maxWidth: "100%",
            overflow: "hidden",
          }}
        >
          {isAssessment && (
            <Box sx={{ px: 0.5, pt: 0.5, pb: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 1 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#292D5B", letterSpacing: "0.1px" }}>
                  {activeCatLabel || "Adaptive Aptitude"}
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#8A90A3" }}>
                  {activeCatLabel || "Aptitude Test"} - Section {sectionNumber} / {CATEGORY_LIST.length} - {categoryProgress}%
                </Typography>
              </Box>
              <Box sx={{ height: 6, borderRadius: 99, backgroundColor: "#EAECF3", overflow: "hidden" }}>
                <Box
                  sx={{
                    height: "100%",
                    width: `${categoryProgress}%`,
                    borderRadius: 99,
                    background: "linear-gradient(90deg, #F6A03A 0%, #FFC873 100%)",
                    transition: "width .5s ease",
                  }}
                />
              </Box>
            </Box>
          )}
          <Box
            sx={
              isAssessment
                ? {
                    display: "flex",
                    gap: 0.75,
                    flexWrap: "wrap",
                    px: 0.5,
                    pb: 0.5,
                  }
                : {
                    display: "flex",
                    gap: 1,
                    overflowX: "auto",
                    overflowY: "hidden",
                    py: 1,
                    px: 1,
                    height: "90px",
                    scrollBehavior: "smooth",
                    "&::-webkit-scrollbar": { height: 6 },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#ccc",
                      borderRadius: 10,
                    },
                  }
            }
          >
            {CATEGORY_LIST.map((cat, index) => {
              // âœ… Currently active category (being tested right now)
              const isActive = cat.key === selectedCategory;

              // âœ… Previously completed categories (before the current one)
              const isDone =
                allSelectedKeys.includes(cat.key) && !isActive;

              // âœ… Future categories not yet reached
              const isFuture = !allSelectedKeys.includes(cat.key);

              if (isAssessment) {
                return (
                  <Box
                    key={cat.key}
                    ref={(el) => (cardRefs.current[index] = el)}
                    title={cat.label}
                    sx={{
                      flexShrink: 0,
                      width: 34,
                      height: 7,
                      borderRadius: 99,
                      transition: "all .3s ease",
                      backgroundColor: isActive
                        ? "#F6A03A"
                        : isDone
                          ? "#2E7D32"
                          : "#E2E5EE",
                      opacity: isFuture ? 0.7 : 1,
                    }}
                  />
                );
              }

              return (
                <Box
                  key={cat.key}
                  ref={(el) => (cardRefs.current[index] = el)}
                  // âœ… No onClick â€” backend auto-selects categories
                  sx={{
                    position: "relative",
                    minWidth: 132,
                    height: 60,
                    flexShrink: 0,
                    cursor: "default",
                    opacity: isActive ? 1 : isDone ? 0.7 : 0.3,
                    transition: "opacity 0.3s ease",
                  }}
                >
                  {/* Background shape */}
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      borderRadius: "0px 55px 0 55px",
                      background: isDone
                        ? "#9dcdcc"   // completed â†’ teal
                        : isActive
                          ? "#f5c994"   // active â†’ orange
                          : "#FBEAC8",  // future â†’ light
                      transition: "background 0.3s ease",
                    }}
                  />

                  {/* Card */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 5,
                      left: 10,
                      background: isDone ? "#e8f7f7" : "#fff",
                      color: "#000",
                      borderRadius: "10px",
                      p: 1.2,
                      textAlign: "center",
                      height: "50px",
                      width: "110px",
                      boxShadow: isActive
                        ? "0 6px 16px rgba(0,0,0,0.18)"
                        : "0 4px 10px rgba(0,0,0,0.1)",
                      fontWeight: 600,
                      fontSize: "12px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "box-shadow 0.3s ease, background 0.3s ease",
                      border: isActive
                        ? "2px solid #9dcdcc"
                        : "2px solid transparent",
                    }}
                  >
                    {cat.label}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* -------------------- Messages -------------------- */}
      <div className="messages-container">
        {messages.length === 0 ? (
          <p className="no-messages">No messages yet. Start the conversation!</p>
        ) : (
          messages?.map((msg, index) => {
            const questionNumber =
              msg.sender === "system" && msg.message_type === "question"
                ? messages
                  .slice(0, index + 1)
                  .filter(
                    (m) =>
                      m.sender === "system" && m.message_type === "question"
                  ).length
                : null;

            return (
              <div
                key={index}
                id={`message-${index}`}
                className={`message-wrapper ${msg.sender === "student" ? "student" : "system"
                  }`}
              >
                <div
                  className={`message ${msg.sender}${
                    isAssessment && msg.message_type === "question" ? " q" : ""
                  }${
                    isAssessment && msg.message_type === "explanation"
                      ? msg.content.includes("Correct answer")
                        ? " exp-correct"
                        : " exp-wrong"
                      : ""
                  }`}
                  style={{
                    backgroundColor:
                      msg.message_type === "explanation"
                        ? msg.content.includes("Correct answer")
                          ? "#00FF9533"
                          : "#FF000033"
                        : "#FFF",
                  }}
                >
                  <Typography
                    onCopy={(e) => e.preventDefault()}
                    onCut={(e) => e.preventDefault()}
                    onContextMenu={(e) => e.preventDefault()}
                    onKeyDown={(e) => {
                      if (
                        (e.ctrlKey || e.metaKey) &&
                        ["c", "x", "a"].includes(e.key.toLowerCase())
                      ) {
                        e.preventDefault();
                      }
                    }}
                    sx={{
                      padding: "2px",
                      whiteSpace: "pre-line",
                      fontSize: isAssessment ? "15px" : "20px",
                      lineHeight: isAssessment ? 1.6 : 1.5,
                      color: isAssessment ? "#2C3350" : undefined,
                      // Highlight greeting / category / completion messages
                      ...(msg.sender === "system" &&
                        ["greeting", "category_selection", "completion"].includes(
                          msg.message_type
                        )
                        ? { fontWeight: 700, color: "#292D5B" }
                        : {}),
                    }}
                  >
                    {msg.sender === "system" &&
                      msg.message_type === "question" ? (
                      <>
                        <strong>{`Q${questionNumber}. `}</strong>
                        {msg.content}
                      </>
                    ) : (
                      msg.content
                    )}
                  </Typography>
                </div>

                {msg.sender === "student" && (
                  <div className="icon">
                    <img
                      src={
                        student?.avatar ||
                        "/assets/images/news/profile-pic.png"
                      }
                      alt="User"
                      className="profile-image"
                    />
                  </div>
                )}
              </div>
            );
          })
        )}

        {currentQuestion?.image_url && (
          <div style={isAssessment ? { marginTop: 8 } : undefined}>
            <img
              width={350}
              height={350}
              src={currentQuestion?.image_url}
              alt="question"
              style={
                isAssessment
                  ? {
                      borderRadius: 16,
                      border: "1px solid #E7EAF1",
                      objectFit: "contain",
                      background: "#fff",
                      padding: 8,
                    }
                  : undefined
              }
            />
          </div>
        )}
      </div>

      {/* -------------------- Options -------------------- */}
      {!isCategorySelection && currentQuestion?.options && (
        <div className="option-container">
          {isAssessment ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1.25,
                width: "100%",
                m: "6px 4px",
              }}
            >
              {Object.entries(currentQuestion?.options).map(
                ([key, val], index) => (
                  <Box
                    key={key}
                    onClick={() => !isLoadingChat && handleOptionClick(key)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1.85,
                      borderRadius: "14px",
                      border: "1.5px solid #E7EAF1",
                      backgroundColor: "#fff",
                      cursor: isLoadingChat ? "not-allowed" : "pointer",
                      opacity: isLoadingChat ? 0.6 : 1,
                      transition: "all .18s ease",
                      "&:hover": isLoadingChat
                        ? {}
                        : {
                            borderColor: "#F6A03A",
                            backgroundColor: "#FFF9F0",
                            transform: "translateY(-1px)",
                            boxShadow: "0 8px 20px rgba(246,160,58,0.16)",
                          },
                    }}
                  >
                    <Box
                      sx={{
                        flexShrink: 0,
                        width: 30,
                        height: 30,
                        borderRadius: "9px",
                        backgroundColor: "#F1F0F8",
                        color: "#292D5B",
                        fontSize: 13.5,
                        fontWeight: 800,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {optionLetter(index)}
                    </Box>
                    <Typography sx={{ fontSize: 14.5, fontWeight: 500, color: "#2C3350", lineHeight: 1.45 }}>
                      {val}
                    </Typography>
                  </Box>
                )
              )}
            </Box>
          ) : (
            <Box display={"flex"} flexWrap={"wrap"} gap={2} margin={"5px 15px"}>
              {Object.entries(currentQuestion?.options).map(
                ([key, val], index) => (
                  <Chip
                    key={key}
                    label={`${index + 1}) ${val}`}
                    color="default"
                    size="16px"
                    variant="filled"
                    disabled={isLoadingChat}
                    onClick={() => handleOptionClick(key)}
                  />
                )
              )}
            </Box>
          )}
        </div>
      )}

      {/* -------------------- Text Input -------------------- */}
      {!isTestCompleted && !["adaptive", "personality"].includes(selectSubject)  && (
        <div className="input-container">
          <input
            type="text"
            placeholder="Answer the question..."
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            className="input"
            autoFocus
            onKeyDown={(e) => e.key === "Enter" && handleTextSubmit()}
            disabled={isLoadingChat || isTestCompleted}
          />
          <IconButton disabled={isLoadingChat}>
            <MoreHoriz />
          </IconButton>
          <IconButton disabled={isLoadingChat} onClick={handleTextSubmit}>
            <Send />
          </IconButton>
        </div>
      )}

      {/* -------------------- Submit Button -------------------- */}
      <Box
        sx={{
          mt: isAssessment ? 3 : 4,
          pt: isAssessment ? 2 : 0,
          borderTop: isAssessment ? "1px solid #EEF0F5" : "none",
          display: "flex",
          justifyContent: "end",
          alignItems: "center",
          gap: 2,
          mb: isAssessment ? 1 : 4,
        }}
      >
        {isAssessment && !isTestCompleted && (
          <Typography sx={{ fontSize: 12.5, color: "#8A90A3", fontWeight: 600 }}>
            {answeredQuestions > 0
              ? `${answeredQuestions} answered - keep going`
              : "Answer the questions to continue"}
          </Typography>
        )}
        <CommonButtonActionButton
          onClick={onComplete}
          disabled={!isTestCompleted}
          sx={{
            width: 150,
            ...(isAssessment && {
              borderRadius: "12px",
              fontWeight: 700,
              boxShadow: isTestCompleted ? "0 10px 22px rgba(246,160,58,0.32)" : "none",
            }),
          }}
        >
          SUBMIT
        </CommonButtonActionButton>
      </Box>
    </div>
  );
};

export default Chatbot;
